import { useEffect, useState } from 'react';
import { track } from './analytics';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    for (const l of listeners) l();
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    for (const l of listeners) l();
  });
}

const isStandalone = (): boolean =>
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true);

const isIos = (): boolean =>
  typeof navigator !== 'undefined' && /iphone|ipad|ipod/i.test(navigator.userAgent);

/**
 * "Get the app" without an app store: the site is an installable PWA. Chromium exposes a prompt;
 * iOS needs Share → Add to Home Screen, so we show instructions there instead.
 */
export function useInstallPrompt(): {
  canInstall: boolean;
  installed: boolean;
  iosHint: boolean;
  prompt: () => Promise<void>;
} {
  const [, rerender] = useState(0);
  useEffect(() => {
    const l = (): void => rerender((n) => n + 1);
    listeners.add(l);
    return () => {
      listeners.delete(l);
    };
  }, []);
  const installed = isStandalone();
  return {
    canInstall: deferred !== null && !installed,
    installed,
    iosHint: isIos() && !installed,
    async prompt() {
      if (!deferred) return;
      track('install_prompted');
      await deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice.outcome === 'accepted') deferred = null;
      for (const l of listeners) l();
    },
  };
}
